import React, { useCallback, useEffect } from 'react';

import styled from 'styled-components';
import { dataLayer } from '@weatherbug/common';
import { reduxConnect } from '../../../common';
import { useInterval } from '../../../common/hooks/useInterval';
import { smallMediaQuery } from '../../../common/utilities/sizeHelpers';
import { TriangleBadgeIcon }  from '../../common/icons/triangleBadgeIcon';
import { NotificationsStatus, getNotificationsStatus } from '../../alerts/alertsInterfaces';
import { IStdParameters } from '../../common/interfaces/types';

const verbose = false;
const refresh = true; 

interface LocationAlertProps {
    notificationsStatus: NotificationsStatus[];
    latitude: number; 
    longitude: number; 
    emLocationId: string;
    notificationsBaseUrl: string; 
    authId: string; 
    secret: string; 
    stdParameters: IStdParameters; 
    getNotificationsStatus: getNotificationsStatus;
    alertsPollerDelaySeconds: number;
}

const mapStoreStateToProps = (storeState: any) => {
    const { 
        notificationsBaseUrl, 
        authId, 
        secret, 
        stdParameters, 
        alertsPollerDelaySeconds 
    } = storeState?.appState?.appState || {};
    
    return {
        notificationsStatus: storeState?.alerts?.notificationsStatus,
        notificationsBaseUrl,
        authId,
        secret,
        stdParameters,
        alertsPollerDelaySeconds
    };
};

const mapDispatchToProps = {
    getNotificationsStatus: dataLayer.actions.getNotificationsStatus
};

//TECHDEBT: This initially calls the API twice on load instead of once because authId/secret/etc. get unset and then re-set.  
// Need to figure out what is causing that, but think it's a larger issue on the site with redux.
export const LocationAlert = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({ 
    notificationsStatus,
    latitude, 
    longitude, 
    emLocationId,
    notificationsBaseUrl, 
    authId, 
    secret, 
    stdParameters, 
    getNotificationsStatus, 
    alertsPollerDelaySeconds 
}: LocationAlertProps) => {
    const locationAlertInfo = notificationsStatus?.find(x => x.eml === emLocationId);
    const backgroundFillColor = locationAlertInfo?.nb?.ids?.sbgd?.afc?.ch6;
    const backgroundFillOpacity = locationAlertInfo?.nb?.ids?.sbgd?.afc?.opa;
    const totalNotificationCount = locationAlertInfo?.tnc;

    const fetchNotificationsStatus = useCallback((notificationsBaseUrl, authId, latitude, longitude, secret, stdParameters) => {
        try {
            if (notificationsBaseUrl && authId && latitude && longitude && secret) {
                getNotificationsStatus(
                    latitude, 
                    longitude, 
                    notificationsBaseUrl, 
                    authId, 
                    secret,
                    stdParameters, 
                    verbose, 
                    refresh, 
                    emLocationId
                );
            }
        } catch (error) {
            console.log(error);
        }
    }, [notificationsBaseUrl, authId, latitude, longitude, secret, stdParameters]);

    useInterval(
        () => {
            if (notificationsBaseUrl && authId && latitude && longitude && secret && stdParameters) {
                fetchNotificationsStatus(notificationsBaseUrl, authId, latitude, longitude, secret, stdParameters);
            }
        },
        alertsPollerDelaySeconds * 1000,
        true,
        !!(notificationsBaseUrl && authId && latitude && longitude && secret && stdParameters),
        [notificationsBaseUrl, authId, latitude, longitude, secret, stdParameters]
    );

    return (
        <Container>
            <TriangleBadgeIcon 
                backgroundFillColor={backgroundFillColor}
                backgroundFillOpacity={backgroundFillOpacity}
            />
            <TextContainer>
                { totalNotificationCount ? totalNotificationCount :  0 }
            </TextContainer>
        </Container>
    );
});

const Container = styled.div`
    position: relative;
    padding-left: 16px;
    padding-right: 16px;

    @media${smallMediaQuery} {
        padding-left: 4px;
        padding-right: 4px;
    }
`;

const TextContainer = styled.span`
    position: absolute;
    margin-top: 7px;
    left: 42.5%;
    display: inline-block;
    color: #FFFFFF;
    font-weight: 600;
    font-size: 17px;

    @media${smallMediaQuery} {
        left: 39%;
    }
`;
